import {
    Button,
    CircularProgress,
    Collapse,
    LinearProgress,
    Typography,
    makeStyles,
} from '@material-ui/core'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import AIIcon from 'resources/AIIcon'
import type { SpecIntelligenceRunStatus } from 'api/graphQL/types/specIntelligence.types'
import React, { useEffect, useRef, useState } from 'react'
import {
    SPEC_INT_ERROR_COLOR,
    SPEC_INT_ICON_CIRCLE_BACKGROUND_COLOR,
    SPEC_INT_ICON_CIRCLE_COLOR,
    SPEC_INT_MUTED_TEXT_COLOR,
    SPEC_INT_NAVY_COLOR,
    SPEC_INT_SECONDARY_TEXT_COLOR,
} from './specIntelligenceConstants'

const useStyles = makeStyles(() => ({
    '@keyframes starSpin': {
        '0%': { transform: 'rotate(0deg)' },
        '10%': { transform: 'rotate(113deg)' },
        '20%': { transform: 'rotate(216deg)' },
        '30%': { transform: 'rotate(309deg)' },
        '40%': { transform: 'rotate(395deg)' },
        '50%': { transform: 'rotate(477deg)' },
        '60%': { transform: 'rotate(563deg)' },
        '70%': { transform: 'rotate(656deg)' },
        '80%': { transform: 'rotate(824deg)' },
        '90%': { transform: 'rotate(1088deg)' },
        '100%': { transform: 'rotate(1440deg)' },
    },
    '@keyframes starPulse': {
        '0%': { transform: 'scale(0.9)' },
        '50%': { transform: 'scale(1.12)' },
        '100%': { transform: 'scale(0.9)' },
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        padding: `${pxToRem(32)} ${pxToRem(24)}`,
        gap: pxToRem(20),
        textAlign: 'center',
    },
    iconCircle: {
        width: pxToRem(56),
        height: pxToRem(56),
        borderRadius: '50%',
        backgroundColor: SPEC_INT_ICON_CIRCLE_BACKGROUND_COLOR,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: SPEC_INT_ICON_CIRCLE_COLOR,
    },
    icon: {
        width: pxToRem(32),
        height: pxToRem(32),
        animation: '$starPulse 2.4s ease-in-out infinite',
        '& path': {
            transformBox: 'fill-box',
            transformOrigin: 'center',
            animation: '$starSpin 6.8s linear infinite',
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            '& path': { animation: 'none' },
        },
    },
    title: {
        fontSize: pxToRem(15),
        fontWeight: 600,
        color: SPEC_INT_NAVY_COLOR,
    },
    progressBar: {
        width: '100%',
        height: pxToRem(6),
        borderRadius: pxToRem(3),
    },
    progressLabel: {
        fontSize: pxToRem(13),
        color: SPEC_INT_MUTED_TEXT_COLOR,
    },
    errorLabel: {
        fontSize: pxToRem(13),
        color: SPEC_INT_ERROR_COLOR,
    },
    cancelButton: {
        textTransform: 'none',
        fontSize: pxToRem(13),
        color: SPEC_INT_SECONDARY_TEXT_COLOR,
        borderColor: '#BDBDBD',
    },
    confirmRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: pxToRem(8),
    },
    confirmMessage: {
        fontSize: pxToRem(13),
        color: SPEC_INT_SECONDARY_TEXT_COLOR,
    },
    confirmActions: {
        display: 'flex',
        justifyContent: 'center',
        gap: pxToRem(8),
    },
    discardButton: {
        textTransform: 'none',
        fontSize: pxToRem(13),
        backgroundColor: SPEC_INT_ERROR_COLOR,
        color: '#FFFFFF',
        '&:hover': { backgroundColor: '#A31F1F' },
    },
}))

/** Shown in place of the Cancel button once the user asks to cancel. */
const CANCEL_CONFIRM_MESSAGE = 'Discard this analysis? Progress will be lost.'

/** Ties {@link CANCEL_CONFIRM_MESSAGE} to the Discard button as its accessible description. */
const CANCEL_CONFIRM_MESSAGE_ID = 'spec-intel-cancel-confirm-message'

interface CancelAnalysisActionProps {
    /** Fired when the user confirms the discard. */
    onCancel: () => void
    /** True while the cancel request is in flight. */
    isCanceling: boolean
}

/**
 * Two-step cancel control. The first click swaps the button for an inline confirmation row
 * rather than opening a dialog, so the progress bar stays visible while the user decides and
 * nothing steals focus out of the drawer.
 */
const CancelAnalysisAction = ({ onCancel, isCanceling }: CancelAnalysisActionProps) => {
    const classes = useStyles()
    const [isConfirming, setIsConfirming] = useState(false)
    const discardRef = useRef<HTMLButtonElement>(null)

    // An inline swap is not announced the way a dialog is, so move focus onto the destructive
    // action as it appears — keyboard and screen-reader users land where sighted users look, and
    // aria-describedby means the confirmation message is read out with it. A live region is not
    // used on purpose: it would have to be a persistent wrapper to announce reliably, and it would
    // then double up with this focus announcement.
    useEffect(() => {
        if (isConfirming) discardRef.current?.focus()
    }, [isConfirming])

    return (
        <>
            {!isConfirming && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsConfirming(true)}
                    className={classes.cancelButton}
                >
                    Cancel
                </Button>
            )}
            <Collapse in={isConfirming} unmountOnExit>
                <div className={classes.confirmRow}>
                    <Typography
                        id={CANCEL_CONFIRM_MESSAGE_ID}
                        className={classes.confirmMessage}
                    >
                        {CANCEL_CONFIRM_MESSAGE}
                    </Typography>
                    <div className={classes.confirmActions}>
                        <Button
                            ref={discardRef}
                            variant="contained"
                            size="small"
                            onClick={onCancel}
                            disabled={isCanceling}
                            aria-busy={isCanceling}
                            aria-describedby={CANCEL_CONFIRM_MESSAGE_ID}
                            className={classes.discardButton}
                            startIcon={
                                isCanceling ? (
                                    <CircularProgress size={12} aria-hidden="true" />
                                ) : undefined
                            }
                            disableElevation
                        >
                            Discard analysis
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setIsConfirming(false)}
                            disabled={isCanceling}
                            className={classes.cancelButton}
                        >
                            Keep running
                        </Button>
                    </div>
                </div>
            </Collapse>
        </>
    )
}

/** Props for {@link SpecIntelligenceProgressView}. */
export interface SpecIntelligenceProgressViewProps {
    /** Current run status from the polling hook; null while waiting for first response. */
    status: SpecIntelligenceRunStatus | null
    /** When provided, a two-step Cancel control is shown below the progress bar. */
    onCancel?: () => void
    /** True while the cancel request is in flight — disables the confirm actions. */
    isCanceling?: boolean
}

/**
 * Displays a progress bar and status message while a SpecIntelligence run is building.
 * Polling is managed externally by {@link useSpecIntelligencePolling}.
 */
const SpecIntelligenceProgressView = ({
    status,
    onCancel,
    isCanceling = false,
}: SpecIntelligenceProgressViewProps) => {
    const classes = useStyles()
    const progress = status?.progressPercentage ?? 0

    return (
        <div className={classes.root}>
            <div className={classes.iconCircle} aria-hidden="true">
                <AIIcon className={classes.icon} />
            </div>
            <Typography component="h3" className={classes.title}>Analyzing your project knowledge base...</Typography>
            <LinearProgress
                variant="determinate"
                value={progress}
                className={classes.progressBar}
                aria-label="Analysis progress"
            />
            {status?.state === 'Error' ? (
                <Typography className={classes.errorLabel} role="alert">
                    An error occurred during analysis.
                </Typography>
            ) : (
                <Typography className={classes.progressLabel}>
                    {status?.statusMessage ?? `${progress}% complete`}
                </Typography>
            )}
            {onCancel && (
                <CancelAnalysisAction onCancel={onCancel} isCanceling={isCanceling} />
            )}
        </div>
    )
}

export default SpecIntelligenceProgressView
